import { SpriteLabRequest, SpriteLabState } from "game-common/spritelab"
import { Mouse } from "./rendering/impl/pixijs/input/mouse"
import { Keyboard } from "./rendering/impl/pixijs/input/keyboard"

/** Native controls keep the small, scrollable panel usable with touch and a soft keyboard. */
export class SpriteLabWidget {
    private root: HTMLDivElement
    private body: HTMLElement
    private counts: HTMLDivElement
    private message: HTMLDivElement
    private prompt: HTMLTextAreaElement
    private kind: HTMLSelectElement
    private catalog: HTMLSelectElement
    private generate: HTMLButtonElement
    private spawn: HTMLButtonElement
    private toggle: HTMLButtonElement
    private minimized = false
    private state: SpriteLabState | null = null
    private catalogKey = ""
    private x = 12
    private y = 90
    private drag: { id: number; x: number; y: number } | null = null

    constructor(private send: (request: SpriteLabRequest) => void, private onInteraction: () => void = () => {}) {
        this.root = document.createElement("div")
        this.root.className = "spritelab-widget"
        this.root.setAttribute("aria-label", "SpriteLab")
        this.root.innerHTML = `<style>
.spritelab-widget {position:fixed;z-index:45;box-sizing:border-box;background:#20262e;color:#ece7dd;border:1px solid #827458;border-radius:8px;font:12px Arial,sans-serif;overflow:hidden;box-shadow:0 3px 12px #0008;}
.spritelab-widget * {box-sizing:border-box;}
.spritelab-widget header {height:28px;display:flex;align-items:center;gap:8px;padding:3px 7px;background:#343b43;cursor:grab;touch-action:none;user-select:none;}
.spritelab-widget header strong {flex:1;}
.spritelab-widget button,.spritelab-widget select,.spritelab-widget textarea {font:inherit;color:inherit;border:1px solid #6a727b;border-radius:4px;background:#303943;}
.spritelab-widget button {padding:4px 7px;cursor:pointer;}
.spritelab-widget button:disabled {opacity:.5;cursor:default;}
.spritelab-widget button:focus-visible,.spritelab-widget select:focus-visible,.spritelab-widget textarea:focus-visible {outline:2px solid #dfbf75;outline-offset:-2px;}
.spritelab-widget main {padding:7px;overflow:auto;}
.spritelab-widget label {display:block;margin:6px 0 3px;}
.spritelab-widget textarea {width:100%;height:44px;resize:none;padding:4px;font-size:16px;}
.spritelab-widget select {max-width:100%;min-width:0;padding:3px;}
.spritelab-widget .row {display:flex;gap:5px;align-items:center;}
.spritelab-widget .row select {flex:1;}
.spritelab-widget .counts {color:#e2c583;font-size:11px;}
.spritelab-widget .message {margin-top:5px;font-size:11px;overflow-wrap:anywhere;}
</style><header><strong>SpriteLab</strong><button type="button" aria-label="Minimize SpriteLab" aria-expanded="true">−</button></header>
<main><div class="counts" role="status" aria-live="polite"></div>
<label for="spritelab-kind">Create</label><div class="row"><select id="spritelab-kind"><option value="creature">Creature</option><option value="equipment">Equipment</option></select><button type="button" data-action="make">Generate</button></div>
<label for="spritelab-prompt">Description</label><textarea id="spritelab-prompt" maxlength="4000" placeholder="A tiny blue dragon…"></textarea>
<label for="spritelab-catalog">Existing creations</label><div class="row"><select id="spritelab-catalog"></select><button type="button" data-action="spawn">Spawn</button></div>
<div class="message" role="status" aria-live="polite"></div><button type="button" data-action="leave" style="margin-top:6px">Leave room</button></main>`
        this.body = this.root.querySelector("main")!
        this.counts = this.root.querySelector(".counts")!
        this.message = this.root.querySelector(".message")!
        this.prompt = this.root.querySelector("textarea")!
        this.kind = this.root.querySelector("#spritelab-kind")!
        this.catalog = this.root.querySelector("#spritelab-catalog")!
        this.generate = this.root.querySelector('[data-action="make"]')!
        this.spawn = this.root.querySelector('[data-action="spawn"]')!
        this.toggle = this.root.querySelector("header button")!
        this.generate.onclick = () => {
            const prompt = this.prompt.value.trim()
            if (!prompt || !this.state || this.generate.disabled) return
            this.generate.disabled = true
            this.message.textContent = "Starting generation…"
            this.send({ action: "make", kind: this.kind.value as "creature" | "equipment", prompt })
        }
        this.prompt.oninput = () => this.updateButtons()
        this.spawn.onclick = () => {
            const entry = this.state?.entries[Number(this.catalog.value)]
            if (entry) this.send({ action: "spawn", ...entry })
        }
        this.root.querySelector<HTMLButtonElement>('[data-action="leave"]')!.onclick = () => this.send({ action: "leave" })
        this.toggle.onclick = () => {
            this.minimized = !this.minimized
            this.toggle.textContent = this.minimized ? "+" : "−"
            this.root.querySelector("header strong")!.textContent = this.headerTitle()
            this.toggle.setAttribute("aria-label", `${this.minimized ? "Expand" : "Minimize"} SpriteLab`)
            this.toggle.setAttribute("aria-expanded", String(!this.minimized))
            this.blur()
            this.layout()
        }
        const claimInput = () => {
            // Browser pointer events also synthesize mouse events. The world listens to
            // document mousedown/up, so both families must stay inside this panel.
            Mouse.state.buttons.clear()
            this.onInteraction()
        }
        this.root.addEventListener("pointerenter", claimInput)
        this.root.addEventListener("focusin", () => { claimInput(); Keyboard.suppressTyping(true) })
        this.root.addEventListener("focusout", event => {
            if (!this.root.contains(event.relatedTarget as Node)) Keyboard.suppressTyping(false)
        })
        for (const name of ["keydown", "keyup", "paste", "pointerdown", "pointermove", "pointerup", "pointercancel",
            "mousedown", "mousemove", "mouseup", "touchstart", "touchmove", "touchend", "touchcancel",
            "click", "dblclick", "contextmenu", "wheel"])
            this.root.addEventListener(name, event => {
                event.stopPropagation()
                claimInput()
                if (name === "keydown" && (event as KeyboardEvent).key === "Escape") this.blur()
            })
        const header = this.root.querySelector("header")!
        header.addEventListener("pointerdown", event => {
            if ((event.target as HTMLElement).closest("button") || event.button !== 0) return
            this.drag = { id: event.pointerId, x: event.clientX - this.x, y: event.clientY - this.y }
            header.setPointerCapture(event.pointerId)
        })
        header.addEventListener("pointermove", event => {
            if (!this.drag || this.drag.id !== event.pointerId) return
            this.x = event.clientX - this.drag.x
            this.y = event.clientY - this.drag.y
            this.layout()
        })
        header.addEventListener("pointerup", () => { this.drag = null })
        header.addEventListener("pointercancel", () => { this.drag = null })
        window.addEventListener("resize", this.layout)
        this.root.hidden = true
        document.body.appendChild(this.root)
        this.layout()
    }

    private headerTitle() {
        return this.minimized && this.state ? `SpriteLab · G${this.state.generating} P${this.state.publishing} Q${this.state.queued}` : "SpriteLab"
    }

    private blur() {
        if (this.root.contains(document.activeElement)) (document.activeElement as HTMLElement).blur()
    }

    private updateButtons() {
        this.generate.disabled = !this.state?.available || !!this.state.ownPhase || this.state.generating >= 2 ||
            this.state.generating + this.state.queued + this.state.publishing >= 8 || !this.prompt.value.trim()
        this.spawn.disabled = !this.state?.entries.length
    }

    update(state: SpriteLabState | null, message?: string) {
        const changedRoom = this.state?.roomId !== state?.roomId
        this.state = state
        this.root.hidden = !state
        if (!state) { this.blur(); return }
        this.root.querySelector("header strong")!.textContent = this.headerTitle()
        this.counts.textContent = `Generating ${state.generating}/2 · Publishing ${state.publishing} · Queued ${state.queued}`
        if (changedRoom) this.message.textContent = ""
        if (message) this.message.textContent = message
        const key = JSON.stringify(state.entries)
        if (key !== this.catalogKey) {
            const selected = this.catalog.selectedOptions[0]?.textContent
            this.catalog.replaceChildren()
            state.entries.forEach((entry, index) => {
                const option = document.createElement("option")
                option.value = String(index)
                option.textContent = `${entry.id} (${entry.kind})`
                option.selected = option.textContent === selected
                this.catalog.appendChild(option)
            })
            if (!state.entries.length) this.catalog.add(new Option("No published creations yet", ""))
            this.catalogKey = key
        }
        this.updateButtons()
        this.layout()
    }

    // Bound the expanded rectangle to 10% of viewport area. Small screens scroll the body.
    private layout = () => {
        const width = Math.min(320, window.innerWidth - 16)
        const height = this.minimized ? 28 : Math.min(270, Math.floor(window.innerWidth * window.innerHeight * 0.1 / width))
        this.x = Math.max(0, Math.min(this.x, window.innerWidth - width))
        this.y = Math.max(0, Math.min(this.y, window.innerHeight - height))
        Object.assign(this.root.style, { width: `${width}px`, height: `${height}px`, left: `${this.x}px`, top: `${this.y}px` })
        this.body.hidden = this.minimized
        this.body.style.height = `${Math.max(0, height - 30)}px`
    }
}
